/**
 * Analytics hook for the StatelessWorkCardsUpsellModal.
 *
 * Consolidates all FullStory events, arbitrary-event tracking, and
 * filter-click tracking into a single hook so the main component
 * only deals with UI state and rendering.
 */

import { useCallback, useEffect, useRef } from 'react';

import {
  BuildPillTrackClick,
  DateRangeFilter,
} from 'js/bulk_download/work_cards_shared/types';
import { WorkId } from 'js/liswp/types';
import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';
import { trackClick } from 'js/utils/trackClick';
import { fsEvent } from 'js/fullstory';

export interface UseModalAnalyticsOptions {
  sourceWorkId: WorkId;
  isPremium: boolean;
  modalVariant: string;
  topicQuery: string;
  dateRange: DateRangeFilter;
  selectedBuckets: Set<string>;
  relatedWorksCount: number;
  hasActiveFilters: boolean;
  loading: boolean;
}

export interface UseModalAnalyticsResult {
  trackFilterClick: (params: { filterValue: 'open' | 'close' }) => void;
  trackPreviewImpression: () => void;
  buildTrackClick: BuildPillTrackClick;
}

export function useModalAnalytics({
  sourceWorkId,
  isPremium,
  modalVariant,
  topicQuery,
  dateRange,
  selectedBuckets,
  relatedWorksCount,
  hasActiveFilters,
  loading,
}: UseModalAnalyticsOptions): UseModalAnalyticsResult {
  const workIdNum = Number(sourceWorkId);
  const fsNamespace = isPremium ? 'BDPremium' : 'BDUpsell';

  // Debounced search query tracking (1.5s to capture final intent)
  const lastTrackedQuery = useRef<string>('');
  useEffect(() => {
    if (!topicQuery || topicQuery === lastTrackedQuery.current) return;

    const timeout = window.setTimeout(() => {
      lastTrackedQuery.current = topicQuery;
      arbitraryEvents.write('BulkDownloadSearch', {
        work_id: workIdNum,
        query: topicQuery,
        is_premium: isPremium,
        source: 'liswp_modal',
      });
      fsEvent(`${fsNamespace}.SearchUsed`, {
        workId_int: workIdNum,
        isPremium_bool: isPremium,
        variant_str: 'modal',
      });
    }, 1500);

    return () => window.clearTimeout(timeout);
  }, [topicQuery, workIdNum, isPremium, fsNamespace]);

  // Date range change tracking
  const prevDateRange = useRef(dateRange);
  useEffect(() => {
    if (dateRange === prevDateRange.current) return;
    prevDateRange.current = dateRange;
    fsEvent(`${fsNamespace}.DateRangeChanged`, {
      workId_int: workIdNum,
      isPremium_bool: isPremium,
      dateRange_str: dateRange,
      variant_str: 'modal',
    });
  }, [dateRange, workIdNum, isPremium, fsNamespace]);

  // Category toggle tracking
  const prevBucketSize = useRef(selectedBuckets.size);
  useEffect(() => {
    if (selectedBuckets.size === prevBucketSize.current) return;
    prevBucketSize.current = selectedBuckets.size;
    fsEvent(`${fsNamespace}.CategoryToggled`, {
      workId_int: workIdNum,
      isPremium_bool: isPremium,
      activeBucketCount_int: selectedBuckets.size,
      variant_str: 'modal',
    });
  }, [selectedBuckets.size, workIdNum, isPremium, fsNamespace]);

  // Empty results tracking — fire once per transition into empty state
  const prevWasEmpty = useRef(false);
  useEffect(() => {
    const isEmpty = hasActiveFilters && relatedWorksCount === 0 && !loading;
    if (isEmpty && !prevWasEmpty.current) {
      fsEvent(`${fsNamespace}.EmptyResults`, {
        workId_int: workIdNum,
        isPremium_bool: isPremium,
        dateRange_str: dateRange,
        hasQuery_bool: topicQuery.trim().length > 0,
        variant_str: 'modal',
      });
    }
    prevWasEmpty.current = isEmpty;
  }, [
    hasActiveFilters,
    relatedWorksCount,
    loading,
    workIdNum,
    isPremium,
    dateRange,
    topicQuery,
    fsNamespace,
  ]);

  const trackFilterClick = useCallback(
    ({ filterValue }: { filterValue: 'open' | 'close' }) => {
      trackClick({
        source: 'bulk_download_modal_filter_click',
        entityId: workIdNum,
        extra: {
          modalVariant,
          filter_type: 'panel',
          filter_value: filterValue,
          is_premium: isPremium,
        },
      });
      fsEvent(`${fsNamespace}.FilterPanelToggled`, {
        workId_int: workIdNum,
        isPremium_bool: isPremium,
        opening_bool: filterValue === 'open',
        variant_str: 'modal',
      });
    },
    [workIdNum, modalVariant, isPremium, fsNamespace],
  );

  const trackPreviewImpression = useCallback(() => {
    fsEvent('bulk_download_upsell_modal_preview.impression', {
      modalVariant_str: modalVariant,
      isPremium_bool: isPremium,
      bundleSize_int: relatedWorksCount,
    });
  }, [modalVariant, isPremium, relatedWorksCount]);

  const buildTrackClick = useCallback<BuildPillTrackClick>(
    (pill) => ({
      source: 'bulk_download_modal_filter_click',
      entityId: workIdNum,
      extra: {
        modalVariant,
        filter_type: pill.filterType,
        filter_value: pill.filterValue,
        is_premium: isPremium,
        ...(pill.action && { action: pill.action }),
      },
    }),
    [workIdNum, modalVariant, isPremium],
  );

  return { trackFilterClick, trackPreviewImpression, buildTrackClick };
}

export default useModalAnalytics;
